import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogType, AuditLogTypeLabels } from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogNarrow } from "@/lib/dialogSize";

// The audit AdditionalData is serialized server-side with PascalCase keys and verbatim enum names
// (e.g. "HashMismatch", "ReplaceFromDisk"), so we humanize the raw string rather than look it up against
// the GUI's camelCase ConflictReason/ConflictResolutionAction enums.
const humanizeEnum = (value: unknown): string =>
  typeof value === "string" ? value.replace(/([a-z])([A-Z])/g, "$1 $2") : "—";

// The parent migration entity the resolved file belongs to, so an operator can jump straight to it instead
// of tracking a bare GUID. Mirrors the ViewMarkNotFoundFileForRetry modal's per-type routing.
const parentEntityLink = (
  organizationId: string,
  eventType: AuditLogType,
  migrationEntityId: string | null | undefined
): string | null => {
  if (!migrationEntityId) return null;
  switch (eventType) {
    case AuditLogType.CaseFileConflictResolved:
      return `/organization/${organizationId}/case/${migrationEntityId}`;
    case AuditLogType.IntakeFileConflictResolved:
      return `/organization/${organizationId}/intake/${migrationEntityId}`;
    case AuditLogType.ProviderFileConflictResolved:
      return `/organization/${organizationId}/provider/${migrationEntityId}`;
    case AuditLogType.TemplateFileConflictResolved:
      return `/organization/${organizationId}/template/${migrationEntityId}`;
    case AuditLogType.FirmDocumentConflictResolved:
      return `/organization/${organizationId}/firm-documents-folder/${migrationEntityId}`;
    default:
      return null;
  }
};

const ViewResolveConflictAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const parsed = (() => {
    if (!auditLog?.additionalData) return null;
    try {
      return JSON.parse(auditLog.additionalData);
    } catch {
      return null;
    }
  })();

  const entityLink = auditLog && parsed
    ? parentEntityLink(organizationId, auditLog.eventType, parsed.MigrationEntityId)
    : null;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogNarrow} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[auditLog.eventType]}</DialogTitle>
            </DialogHeader>

            <Separator />

            {parsed ? (
              <>
                <LabelValuePair label="File" value={parsed.Filename ?? "—"} />
                <LabelValuePair
                  label="Conflict Reason"
                  value={humanizeEnum(parsed.ConflictReason)}
                />
                <LabelValuePair
                  label="Resolution"
                  value={humanizeEnum(parsed.ResolutionAction)}
                />

                <div className="flex flex-row gap-1">
                  <span className="text-sm font-bold">Location:</span>
                  <span className="text-sm">
                    {entityLink ? (
                      <a href={entityLink} className="hover:underline text-primary">
                        {parsed.MigrationEntityId}
                      </a>
                    ) : (
                      (parsed.MigrationEntityId ?? "—")
                    )}
                  </span>
                </div>

                <LabelValuePair label="File ID" value={parsed.FileId ?? auditLog.entityId} />
              </>
            ) : (
              <span className="text-sm">Failed to parse additionalData</span>
            )}

            <LabelValuePair label="Performed By" value={auditLog.performerEmailAddress} />
            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewResolveConflictAuditLogDetailsModal;
